import { useCallback, useRef, useState } from "react";

import axios from "axios";

import {
  acquireCSRFToken,
  defaultApiUrl,
  API_VERSION,
} from "../../../src/libraries/network.implementation";

export interface InvoiceUploadResponse {
  uuid: string;
  status: string;
  original_filename?: string;
}

export type InvoiceUploadStatus = "idle" | "uploading" | "success" | "error";

interface UseInvoiceUploadResult {
  status: InvoiceUploadStatus;
  progress: number;
  response: InvoiceUploadResponse | null;
  upload: (file: File) => Promise<InvoiceUploadResponse | undefined>;
  reset: () => void;
  abort: () => void;
}

const INVOICES_ENDPOINT = `${defaultApiUrl}/${API_VERSION}/invoices/`;

const useInvoiceUpload = (): UseInvoiceUploadResult => {
  const [status, setStatus] = useState<InvoiceUploadStatus>("idle");
  const [progress, setProgress] = useState(0);
  const [response, setResponse] = useState<InvoiceUploadResponse | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    setStatus("idle");
    setProgress(0);
    setResponse(null);
    abortControllerRef.current = null;
  }, []);

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const upload = useCallback(async (file: File) => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setStatus("uploading");
    setProgress(0);
    setResponse(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      // Django session middleware rejects unauthenticated POSTs without an
      // x-csrftoken header. The shared network wrapper adds it for JSON
      // requests, but this multipart path bypasses that wrapper, so acquire
      // the token directly here
      const csrfToken = await acquireCSRFToken();
      const result = await axios.post<InvoiceUploadResponse>(
        INVOICES_ENDPOINT,
        formData,
        {
          signal: controller.signal,
          // Browser sets the multipart boundary itself; do not hand-roll
          // Content-Type
          headers: { Accept: "application/json", "x-csrftoken": csrfToken },
          withCredentials: true,
          onUploadProgress: (event) => {
            const total = event.total ?? file.size;
            if (!total) return;
            const next = Math.min(
              100,
              Math.round((event.loaded / total) * 100)
            );
            setProgress(next);
          },
        }
      );
      setProgress(100);
      setResponse(result.data);
      setStatus("success");
      return result.data;
    } catch (error) {
      if (axios.isCancel?.(error)) {
        // Abort was intentional (e.g. the user removed the file) — leave state
        // alone and don't surface anything to the caller
        return undefined;
      }
      setStatus("error");
      // Re-throw so FileUploader can surface the failure
      throw error;
    } finally {
      abortControllerRef.current = null;
    }
  }, []);

  return { status, progress, response, upload, reset, abort };
};

export default useInvoiceUpload;
